import React, { FC, memo, useCallback } from 'react';
import { Box } from '@withjoy/joykit';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { addRendition } from '@shared/utils/photoRendition';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { AnimatedImageBox } from './PhotoTrio.styles';
import { Photo, PhotoTrioProps } from './PhotoTrio.types';
import { LazyScreen } from '../LazyScreen/LazyScreen';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { pxToRem } from '@withjoy/joykit/theme';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { BrannanPhotoSlider } from '../PhotoSlider';
import { parseAspectRatio } from '@shared/utils/parseAspectRatio';

const ASPECT_RATIO = '6/9';
const GAP = 4;

const PHOTO_COUNT_TO_SHOW = 3;

export const PhotoTrio: FC<PhotoTrioProps> = memo(props => {
  const { photos, showSkeleton = false, showPlaceholder, container } = props;

  const [isMobile] = useResponsive({ values: { xxs: true, xs: false } }, true);

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW);

  const [containerDimensions, containerRef] = useSizeObserver();

  const getPhotoComponent = useCallback(
    (photo: Photo) => {
      if (photo.cropInfo) {
        const width = (containerDimensions.width - GAP * (PHOTO_COUNT_TO_SHOW - 1)) / PHOTO_COUNT_TO_SHOW;
        return (
          <AnimatedImageBox
            key={photo.id}
            width="100%"
            height="auto"
            aspectRatio={ASPECT_RATIO}
          >
            <CroppedImage
              cropInfo={{ ...photo.cropInfo, unit: '%' }}
              imageUrl={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
              height={width / parseAspectRatio(ASPECT_RATIO)}
              width={width}
            />
          </AnimatedImageBox>
        );
      } else {
        return (
          <AnimatedImageBox
            key={photo.id}
            width="100%"
            height="auto"
            aspectRatio={ASPECT_RATIO}
            backgroundImage={backgroundImageForUrl(addRendition({ url: photo.photoUrl, renditionSize: 'medium' }))}
            backgroundSize="cover"
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
          />
        );
      }
    },
    [containerDimensions.width],
  );

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        display="grid"
        ref={containerRef}
        gridTemplateColumns={{ _: 'repeat(1, minmax(0, 1fr))', xs: 'repeat(3, minmax(0, 1fr))' }}
        gap={pxToRem(GAP)}
        width={container === 'viewport' ? '100vw' : '100%'}
        position="relative"
        left={container === 'viewport' ? 'calc(-50vw + 50%)' : '0'}
        overflow="hidden"
      >
        {isMobile ? (
          //  In mobile, we render a photo slider
          <BrannanPhotoSlider
            photos={filledPhotos}
            showPlaceholder={showPlaceholder}
            aspectRatio={parseAspectRatio(ASPECT_RATIO)}
          />
        ) : (
          // PHOTO TRIO render logic
          <>
            {filledPhotos.map(photo => (
              <LazyScreen
                key={photo.id}
                src={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
                width="100%"
                height="auto"
                aspectRatio={ASPECT_RATIO}
                showSkeleton={showSkeleton}
              >
                {() =>
                  photo.photoUrl ? (
                    getPhotoComponent(photo)
                  ) : (
                    <Box
                      width="100%"
                      height="auto"
                      aspectRatio={ASPECT_RATIO}
                      backgroundColor="lightgray"
                      key={photo.id}
                    />
                  )
                }
              </LazyScreen>
            ))}
          </>
        )}
      </Box>
    </>
  );
});

PhotoTrio.displayName = 'PhotoTrio';
